import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, Clock, Tag, ArrowRight } from 'lucide-react';
import { useBlogPosts } from '../../hooks/useBlogPosts';
import { formatDate } from '../../utils/dateUtils';
import BlogPostSkeleton from '../../components/shared/BlogPostSkeleton';
import MarketingReveal from '../../components/home/MarketingReveal';
import {
  MarketingPage,
  MarketingMediaHero,
  MarketingSection,
  MarketingSplitIntro,
} from '../../components/home/MarketingPageChrome';

const BLOG_HERO =
  'https://images.unsplash.com/photo-1478146896981-b80fe463b330?auto=format&fit=crop&w=1800&q=80';

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const { data: postsResponse, isLoading, error } = useBlogPosts({
    limit: 20,
    category: selectedCategory !== 'All' ? selectedCategory : undefined,
  });

  const blogPosts = useMemo(() => {
    if (!postsResponse?.data) return [];
    return Array.isArray(postsResponse.data) ? postsResponse.data : [];
  }, [postsResponse]);

  const categories = useMemo(() => {
    const uniqueCategories = ['All'];
    blogPosts.forEach((post) => {
      if (post.category && !uniqueCategories.includes(post.category)) {
        uniqueCategories.push(post.category);
      }
    });
    return uniqueCategories;
  }, [blogPosts]);

  const featuredPost = useMemo(
    () => blogPosts.find((post) => post.is_featured) || null,
    [blogPosts]
  );

  const regularPosts = useMemo(
    () => blogPosts.filter((post) => !post.is_featured),
    [blogPosts]
  );

  return (
    <MarketingPage>
      <MarketingMediaHero
        title="Event planning ideas"
        subtitle="Tips, guides, and inspiration — from weddings to corporate gatherings."
        image={BLOG_HERO}
        height="sm"
      />

      <MarketingSection tone="white">
        <MarketingSplitIntro title="Resources & inspiration">
          <p>
            Practical guides for planning celebrations — browse by topic or start with a featured
            story.
          </p>
        </MarketingSplitIntro>
      </MarketingSection>

      <div className="sticky top-16 z-10 border-b border-m-border-light bg-white/95 backdrop-blur-sm">
        <div className="mx-auto max-w-6xl px-4 py-3 sm:px-6">
          <div className="flex flex-wrap items-center justify-center gap-2">
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => setSelectedCategory(category)}
                className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-m-ink text-white'
                    : 'text-m-ink-muted hover:bg-m-canvas-light hover:text-m-ink'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isLoading ? (
        <MarketingSection tone="canvas">
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <BlogPostSkeleton key={i} />
            ))}
          </div>
        </MarketingSection>
      ) : null}

      {error && !isLoading ? (
        <MarketingSection tone="canvas">
          <p className="text-center text-sm" style={{ color: '#6b6b6b' }}>
            Failed to load blog posts. Please try again later.
          </p>
        </MarketingSection>
      ) : null}

      {featuredPost && !isLoading ? (
        <MarketingSection tone="canvas">
          <MarketingReveal className="mx-auto max-w-6xl">
            <p
              className="mb-4 text-[11px] font-medium uppercase tracking-[0.16em]"
              style={{ color: '#8a8a8a' }}
            >
              Featured
            </p>
            <Link to={`/blog/${featuredPost.slug}`} className="group block">
              <div className="grid gap-8 md:grid-cols-2 md:items-center">
                <div className="relative h-56 overflow-hidden bg-m-canvas-light sm:h-72 md:h-80">
                  {featuredPost.image ? (
                    <img
                      src={featuredPost.image}
                      alt={featuredPost.title}
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <Tag className="h-12 w-12 text-m-ink-faint" />
                    </div>
                  )}
                </div>
                <div style={{ color: '#0a0a0a' }}>
                  <div
                    className="mb-3 flex flex-wrap items-center gap-3 text-xs"
                    style={{ color: '#8a8a8a' }}
                  >
                    {featuredPost.category ? <span>{featuredPost.category}</span> : null}
                    {featuredPost.read_time ? (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {featuredPost.read_time} min read
                      </span>
                    ) : null}
                  </div>
                  <h2 className="marketing-section-title mb-3 transition-opacity group-hover:opacity-80">
                    {featuredPost.title}
                  </h2>
                  <p className="mb-5 text-[15px] leading-relaxed" style={{ color: '#6b6b6b' }}>
                    {featuredPost.excerpt}
                  </p>
                  <div
                    className="mb-5 flex items-center gap-2 text-sm"
                    style={{ color: '#6b6b6b' }}
                  >
                    <User className="h-4 w-4" />
                    <span>{featuredPost.author_name || 'Admin'}</span>
                    {featuredPost.published_at ? (
                      <>
                        <span>·</span>
                        <span>{formatDate(featuredPost.published_at, 'short')}</span>
                      </>
                    ) : null}
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium">
                    Read article
                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
                  </span>
                </div>
              </div>
            </Link>
          </MarketingReveal>
        </MarketingSection>
      ) : null}

      {!isLoading && regularPosts.length > 0 ? (
        <MarketingSection tone={featuredPost ? 'white' : 'canvas'}>
          <MarketingReveal className="mx-auto grid max-w-6xl grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
            {regularPosts.map((post) => (
              <Link key={post.id} to={`/blog/${post.slug}`} className="group block">
                <div className="relative mb-3 aspect-[4/3] overflow-hidden bg-m-canvas-light">
                  {post.image ? (
                    <img
                      src={post.image}
                      alt={post.title}
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <Tag className="h-10 w-10 text-m-ink-faint" />
                    </div>
                  )}
                </div>
                <div
                  className="mb-2 flex flex-wrap items-center gap-2 text-xs"
                  style={{ color: '#8a8a8a' }}
                >
                  {post.published_at ? (
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(post.published_at, 'short')}
                    </span>
                  ) : null}
                  {post.read_time ? (
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {post.read_time} min
                    </span>
                  ) : null}
                </div>
                <h3
                  className="mb-1.5 line-clamp-2 text-base font-bold tracking-tight transition-opacity group-hover:opacity-80"
                  style={{ color: '#0a0a0a' }}
                >
                  {post.title}
                </h3>
                <p className="line-clamp-2 text-sm" style={{ color: '#6b6b6b' }}>
                  {post.excerpt}
                </p>
              </Link>
            ))}
          </MarketingReveal>
        </MarketingSection>
      ) : null}

      {!isLoading && regularPosts.length === 0 && !featuredPost ? (
        <MarketingSection tone="canvas">
          <p className="text-center text-sm" style={{ color: '#6b6b6b' }}>
            No blog posts found
            {selectedCategory !== 'All' ? ` in ${selectedCategory}` : ''}.
          </p>
        </MarketingSection>
      ) : null}

      <MarketingSection tone="charcoal">
        <MarketingReveal className="mx-auto max-w-lg text-center">
          <h2 className="marketing-section-title mb-3" style={{ color: '#ffffff' }}>
            Stay updated
          </h2>
          <p className="mb-8 text-[1.05rem] leading-relaxed text-white/70">
            Get planning tips and guides in your inbox.
          </p>
          <form
            className="flex flex-col gap-3 sm:flex-row"
            onSubmit={(e) => e.preventDefault()}
          >
            <input
              type="email"
              placeholder="Enter your email"
              className="h-12 flex-1 rounded-full border-0 bg-white px-5 text-sm text-m-ink placeholder:text-m-ink-faint focus:outline-none focus:ring-2 focus:ring-white/40"
            />
            <button type="submit" className="marketing-btn marketing-btn-primary shrink-0">
              Subscribe
            </button>
          </form>
        </MarketingReveal>
      </MarketingSection>
    </MarketingPage>
  );
};

export default Blog;
